// frontend/src/services/api.service.ts

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { ApiResponse } from "@/types";

interface ApiRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
}

type InternalApiRequestConfig = InternalAxiosRequestConfig & {
  skipAuth?: boolean;
};

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    const envUrl = import.meta.env.VITE_API_URL?.replace(/\/+$/, "");
    this.baseURL = envUrl
      ? envUrl.endsWith("/api")
        ? `${envUrl}/v1`
        : envUrl
      : "http://localhost:5000/api/v1";

    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.api.interceptors.request.use(
      (config: InternalApiRequestConfig) => {
        const token = localStorage.getItem("token");

        if (!config.skipAuth && token) {
          (config.headers as any).Authorization = `Bearer ${token}`;
        } else if ((config.headers as any)?.Authorization) {
          delete (config.headers as any).Authorization;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor - Handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as ApiRequestConfig & {
          _retry?: boolean;
        };

        // Handle token expiration
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.skipAuth
        ) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem("refreshToken");
            if (refreshToken) {
              const response = await axios.post(
                `${this.baseURL}/auth/refresh-token`,
                {
                  refreshToken,
                },
              );

              const { token: newToken, refreshToken: newRefreshToken } =
                response.data.data;

              localStorage.setItem("token", newToken);
              localStorage.setItem("refreshToken", newRefreshToken);

              // Retry original request with new token
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
              }
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh token failed - logout user
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");
            localStorage.removeItem("userType");
            alert("Your session has expired. Please log in again.");
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * GET request
   */
  async get<T = any>(
    url: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.put<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.patch<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    url: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.delete<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Upload file(s)
   */
  async upload<T = any>(
    url: string,
    formData: FormData,
    onUploadProgress?: (progressEvent: any) => void,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post<ApiResponse<T>>(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress,
      });
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * GET request without auth header (for public endpoints)
   */
  async getPublic<T = any>(
    url: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.get<T>(url, { ...config, skipAuth: true });
  }

  /**
   * POST request without auth header (for public endpoints)
   */
  async postPublic<T = any>(
    url: string,
    data?: any,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.post<T>(url, data, { ...config, skipAuth: true });
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): ApiResponse {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiResponse>;

      if (axiosError.response) {
        // Server responded with error
        return {
          success: false,
          message: axiosError.response.data.message || "An error occurred",
          error: axiosError.response.data.error,
          status: axiosError.response.status,
        };
      } else if (axiosError.request) {
        // Request made but no response
        return {
          success: false,
          message: "No response from server. Please check your connection.",
          error: "NETWORK_ERROR",
          status: 0,
        };
      }
    }

    // Generic error
    return {
      success: false,
      message: "An unexpected error occurred",
      error: error.message,
      status: 0,
    };
  }

  /**
   * Get base URL
   */
  getBaseURL(): string {
    return this.baseURL;
  }
}

export default new ApiService();
